import React, { useState, useEffect } from "react";
import CleaningSummaryCard from "./CleaningSummaryCard";
import { Sparkles, MapPin, TrendingUp } from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";
import { cleaningApiBaseUrlFromEnv } from "../../runtime/loadSharedDashboardConfig";

interface CleaningMetrics {
  title: string;
  value: string;
  subtitle: string;
}

interface CleaningSummaryCardsProps {
  date?: string;
}

const CleaningSummaryCards: React.FC<CleaningSummaryCardsProps> = ({ date }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";

  const [metrics, setMetrics] = useState<CleaningMetrics[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    const fetchCoverage = async () => {
      setIsLoading(true);
      try {
        const params = new URLSearchParams();
        if (date) params.append("date", date);
        const url = `${cleaningApiBaseUrlFromEnv()}/cleaning/latest-coverage${params.toString() ? "?" + params.toString() : ""}`;
        const response = await fetch(url, {
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          signal: controller.signal,
        });
        if (response.ok) {
          const data = await response.json();
          const items = Array.isArray(data) ? data : (data as any)?.data ?? [];
          setMetrics(items);
        }
      } catch (error) {
        if ((error as DOMException)?.name !== "AbortError") {
          console.error("Failed to fetch cleaning coverage data:", error);
        }
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    };
    fetchCoverage();
    return () => controller.abort();
  }, [date]);

  const processCardData = (apiItem: CleaningMetrics) => {
    const baseProps = {
      title: apiItem.title,
      value: apiItem.value,
      subtitle: apiItem.subtitle,
      subtitleColor: apiItem.subtitle.includes("requires attention") || apiItem.subtitle.includes("needs improvement") ? "text-red-500" : (isDarkMode ? "text-gray-400" : "text-gray-500"),
    };

    if (apiItem.title === "Coverage") {
      return {
        ...baseProps,
        icon: <Sparkles size={24} />,
        iconBgColor: isDarkMode ? "bg-emerald-900/40" : "bg-emerald-100",
        iconColor: isDarkMode ? "text-emerald-400" : "text-emerald-500",
      };
    } else if (apiItem.title === "Missed Areas") {
      return {
        ...baseProps,
        icon: <MapPin size={24} />,
        iconBgColor: isDarkMode ? "bg-amber-900/40" : "bg-amber-100",
        iconColor: isDarkMode ? "text-amber-500" : "text-amber-600",
      };
    } else {
      return {
        ...baseProps,
        icon: <TrendingUp size={24} />,
        iconBgColor: isDarkMode ? "bg-purple-900/40" : "bg-purple-100",
        iconColor: isDarkMode ? "text-purple-400" : "text-purple-500",
      };
    }
  };

  const displayData =
    metrics.length > 0
      ? metrics.map(processCardData)
      : [
          {
            title: "Coverage",
            value: "-",
            subtitle: "loading...",
            subtitleColor: isDarkMode ? "text-gray-400" : "text-gray-500",
            icon: <Sparkles size={24} />,
            iconBgColor: isDarkMode ? "bg-emerald-900/40" : "bg-emerald-100",
            iconColor: isDarkMode ? "text-emerald-400" : "text-emerald-500",
          },
          {
            title: "Missed Areas",
            value: "-",
            subtitle: "loading...",
            subtitleColor: isDarkMode ? "text-gray-400" : "text-gray-500",
            icon: <MapPin size={24} />,
            iconBgColor: isDarkMode ? "bg-amber-900/40" : "bg-amber-100",
            iconColor: isDarkMode ? "text-amber-500" : "text-amber-600",
          },
          {
            title: "Last Cleaned",
            value: "-",
            subtitle: "loading...",
            subtitleColor: isDarkMode ? "text-gray-400" : "text-gray-500",
            icon: <TrendingUp size={24} />,
            iconBgColor: isDarkMode ? "bg-purple-900/40" : "bg-purple-100",
            iconColor: isDarkMode ? "text-purple-400" : "text-purple-500",
          },
        ];

  return (
    <div className={`rounded-2xl p-6 mb-8 ${isDarkMode ? "bg-[#1C202F]/50 border border-gray-800" : "bg-white border border-gray-100 shadow-sm"}`}>
      {/* Section Header */}
      <div className="flex items-start gap-4 mb-6">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDarkMode ? "bg-blue-600/20 text-blue-500" : "bg-blue-100 text-blue-600"}`}>
          <TrendingUp size={22} />
        </div>
        <div>
          <h2 className={`text-xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
            Cleaning - Key Metrics Overview
          </h2>
          <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
            Real-time cleaning statistics and performance indicators
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-start gap-4">
        {displayData.map((card, idx) => (
          <CleaningSummaryCard key={idx} {...card} />
        ))}
      </div>
    </div>
  );
};

export default CleaningSummaryCards;
